import {type GoogleUserProfile, isCiamEnabled} from '@gfiber/pixel-party';
import createClient, {type MaybeOptionalInit} from 'openapi-fetch';
import type {HttpMethod, PathsWithMethod} from 'openapi-typescript-helpers';

import {store as userProfileState} from '@/app/signup/(state)/UserProfileState';

/**
 * Prefixed to the body text responses by the apps framework via Harpoon proxy.
 * Must be stripped to parse the response.
 */
export const JSON_SAFETY_PREFIX_PATTERN = /^\)\]\}'\n/;

export const isRequestMockingEnabled = () => {
  return process.env.NEXT_PUBLIC_MOCK_API_REQUESTS === 'true';
};

if (isRequestMockingEnabled()) {
  console.log('Local mocking of API requests enabled');
}

export interface FetchPostOptions<
  Paths extends Record<string, Record<HttpMethod, {}>>,
> {
  baseUrl: string;
  authHeader?: string;
  path: PathsWithMethod<Paths, 'post'>;
  globalAddrId: string;
  body: MaybeOptionalInit<Paths[PathsWithMethod<Paths, 'post'>], 'post'>;
}

export const fetchPost = async <Paths extends {}>({
  baseUrl,
  path,
  globalAddrId,
  body,
  authHeader,
}: FetchPostOptions<Paths>) => {
  const {POST} = createClient<Paths>({baseUrl});
  const currentUser = userProfileState.user as GoogleUserProfile;
  const AUTH_HEADER_KEY = isCiamEnabled() ? 'X-Auth0-Session' : 'Authorization';

  const {data, error, response} = await POST(path, {
    headers: {
      'Global-Addr-Id': globalAddrId,
      'X-Proxy-Service': 'buyflow',
      'X-GFiber-Email': currentUser?.email,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      'X-Framework-Xsrf-Token': window ? (window as any).gfiberXsrfToken : '',
      [AUTH_HEADER_KEY]: authHeader,
    },
    body,
    parseAs: 'text',
  } as any);

  if (response.status >= 300 || response.status < 100 || error) {
    console.error('Error during Buyflow API request', response.status, error);
    requestErrorProcessing(error, response.status);
  }

  return {data: parseSafeJSONResult(data!), error, response} as const;
};

export const requestErrorProcessing = (error: unknown, status: number) => {
  let rawError = error;

  if (rawError) {
    try {
      if (typeof rawError !== 'string') {
        throw new Error(rawError.toString());
      }
      // Error can be a JSON or NOT
      rawError = parseSafeJSONResult(rawError as string);
    } catch (err) {
      // Dont care about parsing error
      console.warn('Error response parsing', err);
    }
  }

  throw (
    rawError ??
    new Error(`Error during Buyflow API request, status code: ${status}`)
  );
};

export const parseSafeJSONResult = (raw: string) => {
  let dataWithoutPrefix = raw.replace(JSON_SAFETY_PREFIX_PATTERN, '');
  if (dataWithoutPrefix !== raw) {
    // SafeJson needs to be parsed twice
    dataWithoutPrefix = JSON.parse(dataWithoutPrefix);
  }
  return JSON.parse(dataWithoutPrefix);
};
